import React, { useState } from 'react';
import { EyeIcon, FileTextIcon, LockIcon } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { milestones } from '../../data/loans';
import type { Loan } from '../../types/portal';
interface LoanDetailDrawerProps {
  loan: Loan | null;
  onClose: () => void;
  onToast: (message: string) => void;
}
const tabs = [{
  id: 'details',
  label: 'Loan details'
}, {
  id: 'notes',
  label: 'Processor notes'
}, {
  id: 'appraisal',
  label: 'Appraisal'
}, {
  id: 'documents',
  label: 'Documents'
}] as const;
type TabId = (typeof tabs)[number]['id'];
export function LoanDetailDrawer({
  loan,
  onClose,
  onToast
}: LoanDetailDrawerProps) {
  const [tab, setTab] = useState<TabId>('details');
  if (!loan) return null;
  return <Drawer open onClose={onClose} eyebrow={`${loan.id} · ${loan.closingLabel}`} title={loan.borrower} subtitle={`${loan.amount} · ${loan.program}`} data-id="element-337" data-mp="309uj277vluwp">
      <div className="rounded-xl border border-navy-100 bg-navy-50 p-4" data-id="element-338" data-mp="27mqiceftg2qq">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-navy-600" data-id="element-339" data-mp="2fg2v23yn5ss1">
          <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-340" data-mp="2kexlns2uolmp" />
          Processed in house — view only
        </p>
        <ol className="mt-3 flex flex-wrap gap-1.5" data-id="element-341" data-mp="2p7qqefybjtys">
          {milestones.map((milestone, index) => {
          const done = index <= loan.milestoneIndex;
          const current = index === loan.milestoneIndex;
          return <li key={milestone} data-id="element-342" data-mp="21h342pnnp1sp" data-mpi={index}>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${current ? 'bg-brand-600 text-white' : done ? 'bg-teal-100 text-teal-800' : 'bg-white text-navy-500 ring-1 ring-navy-200'}`} aria-current={current ? 'step' : undefined} data-id="element-343" data-mp="21i5u6vd5n07k">
                  {milestone}
                </span>
              </li>;
        })}
        </ol>
        <p className="mt-3 text-xs text-navy-600" data-id="element-344" data-mp="1htwi6d5i8d98">
          Processor: {loan.processor} · Realtor: {loan.realtor}
        </p>
      </div>

      <div role="tablist" aria-label="Loan sections" className="mt-4 flex flex-wrap gap-1 rounded-lg bg-navy-50 p-1" data-id="element-345" data-mp="1vsmgtx7z2kzp">
        {tabs.map(item => {
        const active = tab === item.id;
        return <button key={item.id} role="tab" type="button" aria-selected={active} onClick={() => setTab(item.id)} className={`h-10 flex-1 rounded-md px-3 text-xs font-semibold ${active ? 'bg-white text-navy-900 shadow-card' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-346" data-mp="y5nq033yo79q">
              {item.label}
            </button>;
      })}
      </div>

      <div className="mt-4" data-id="element-347" data-mp="2r1q142h8ly6d">
        {tab === 'details' ? <DetailList items={loan.loanDetails} data-id="element-348" data-mp="2r0x2xpwcggzu" /> : null}

        {tab === 'notes' ? <ul className="space-y-3" data-id="element-349" data-mp="2vuj52o4vt2he">
            {loan.processorNotes.map((note, index) => <li key={index} className="rounded-xl border border-navy-100 bg-white p-4" data-id="element-350" data-mp="2i34laf35gx9a" data-mpi={index}>
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-351" data-mp="2m0smu0zcloa5">
                  <p className="text-sm font-semibold text-navy-900" data-id="element-352" data-mp="39qmtu1wzkimh">
                    {note.author}
                  </p>
                  <p className="text-xs text-navy-500" data-id="element-353" data-mp="30sb74az8q4zb">{note.timestamp}</p>
                </div>
                <p className="mt-1.5 text-sm text-navy-700" data-id="element-354" data-mp="35r4d7ic76tpg">{note.note}</p>
              </li>)}
          </ul> : null}

        {tab === 'appraisal' ? <DetailList items={loan.appraisal} data-id="element-355" data-mp="1pbkvudfymmyu" /> : null}

        {tab === 'documents' ? <ul className="space-y-2" data-id="element-356" data-mp="1udk0rrrclrxw">
            {loan.documents.map(doc => <li key={doc.name} data-id="element-357" data-mp="29mnouphj30j2">
                <button type="button" onClick={() => onToast(`Opening ${doc.name}.`)} className="flex w-full items-center gap-3 rounded-xl border border-navy-100 bg-white p-3 text-left hover:border-brand-200 hover:bg-brand-50/40" data-id="element-358" data-mp="29np290m0nz3y">
                  <FileTextIcon className="h-5 w-5 shrink-0 text-navy-400" aria-hidden="true" data-id="element-359" data-mp="1vs4kw9lxroyn" />
                  <span className="min-w-0 flex-1" data-id="element-360" data-mp="2nmgdmf9hv6oe">
                    <span className="block truncate text-sm font-semibold text-navy-900" data-id="element-361" data-mp="2njkguu9duo1j">
                      {doc.name}
                    </span>
                    <span className="block text-xs text-navy-500" data-id="element-362" data-mp="2ejnmqd484wz3">
                      {doc.type} · uploaded {doc.uploaded} · {doc.status}
                    </span>
                  </span>
                  <EyeIcon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-363" data-mp="135aa64ex83yd" />
                </button>
              </li>)}
          </ul> : null}
      </div>
    </Drawer>;
}
function DetailList({
  items
}: {
  items: {
    label: string;
    value: string;
  }[];
}) {
  return <dl className="divide-y divide-navy-100 rounded-xl border border-navy-100 bg-white" data-id="element-364" data-mp="3nrqsxi9maay8">
      {items.map(item => <div key={item.label} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-id="element-365" data-mp="1e92gqmrbcqw">
          <dt className="text-sm text-navy-500" data-id="element-366" data-mp="6bsinw30lzlh">{item.label}</dt>
          <dd className="text-sm font-semibold text-navy-900" data-id="element-367" data-mp="bcg9agnc0ef6">{item.value}</dd>
        </div>)}
    </dl>;
}